/* global __DEV__ */
import { isDesktop, isMac, isWindows, isLinux } from '@trello/browser';
import {
  analyticsWebClientEnv,
  analyticsWebClientApiHost,
  clientVersion,
  locale,
} from '@trello/config';
import { isValidAction } from './constants/Action';
import { isValidActionSubject } from './constants/ActionSubject';
import { isValidActionSubjectId } from './constants/ActionSubjectId';
import { isValidSource } from './constants/Source';
import AnalyticsWebClient, {
  ApdexStartEvent,
  ApdexStopEvent,
  TaskSessionManager,
  ScreenEvent,
  UIEvent,
  TrackEvent,
  OperationalEvent,
  envType,
  originType,
  platformType,
  AnalyticsWebClientConfig,
  AnalyticsWebClientSettings,
  EventContainer,
} from '@atlassiansox/analytics-web-client';

type EnvironmentVariableType = 'DEV' | 'STAGING' | 'PROD';

const config = {
  env: envType[analyticsWebClientEnv as EnvironmentVariableType],
  product: 'trello',
  version: clientVersion,
  origin: isDesktop() ? originType.DESKTOP : originType.WEB,
  platform: null,
  locale,
} as AnalyticsWebClientConfig;

const settings = {
  apiHost: analyticsWebClientApiHost,
} as AnalyticsWebClientSettings;

if (isDesktop()) {
  switch (true) {
    case isMac():
      config.platform = platformType.MAC;
      break;
    case isWindows():
      config.platform = platformType.WINDOWS;
      break;
    case isLinux():
      config.platform = platformType.LINUX;
      break;
    default:
  }
}

const validateParams = (e: UIEvent | TrackEvent | OperationalEvent) => {
  if (!isValidAction(e.action) && __DEV__) {
    console.error(
      `'${e.action}' is not a valid Action (see packages/atlassian-analytics/src/constants/Action.ts)`,
    );
  }
  if (!isValidActionSubject(e.actionSubject) && __DEV__) {
    console.error(
      `'${e.actionSubject}' is not a valid ActionSubject (see packages/atlassian-analytics/src/constants/ActionSubject.ts)`,
    );
  }
  if (
    e.actionSubjectId &&
    !isValidActionSubjectId(e.actionSubjectId) &&
    __DEV__
  ) {
    console.error(
      `'${e.actionSubjectId}' is not a valid ActionSubjectId (see packages/atlassian-analytics/src/constants/ActionSubjectId.ts)`,
    );
  }
  if (!isValidSource(e.source) && __DEV__) {
    console.error(
      `'${e.source}' is not a valid Source (see packages/atlassian-analytics/src/constants/Source.ts)`,
    );
  }
};

const normalizeContainers = (
  e: ScreenEvent | UIEvent | TrackEvent | OperationalEvent,
) => {
  if (!e.containers) {
    return;
  }

  const normalizedContainers: EventContainer = {};

  if (e.containers.enterprise && e.containers.enterprise.id) {
    normalizedContainers.enterprise = { id: e.containers.enterprise.id };
  }

  if (e.containers.organization && e.containers.organization.id) {
    normalizedContainers.organization = { id: e.containers.organization.id };
  }

  if (e.containers.board && e.containers.board.id) {
    normalizedContainers.board = { id: e.containers.board.id };
  }

  if (e.containers.list && e.containers.list.id) {
    normalizedContainers.list = { id: e.containers.list.id };
  }

  if (e.containers.card && e.containers.card.id) {
    normalizedContainers.card = { id: e.containers.card.id };
  }

  e.containers = normalizedContainers;
};

class AnalyticsWrapper implements AnalyticsWebClient {
  analytics: AnalyticsWebClient;

  constructor(
    clientConfig: AnalyticsWebClientConfig,
    clientSettings?: AnalyticsWebClientSettings,
  ) {
    this.analytics = new AnalyticsWebClient(clientConfig, clientSettings);
  }

  sendScreenEvent(e: ScreenEvent) {
    if (!isValidSource(e.name) && __DEV__) {
      console.error(
        `'${e.name}' is not a valid Source (see packages/atlassian-analytics/src/constants/Source.ts)`,
      );
    }
    normalizeContainers(e);
    this.analytics.sendScreenEvent(e);
  }

  sendUIEvent(e: UIEvent) {
    validateParams(e);
    normalizeContainers(e);
    this.analytics.sendUIEvent(e);
  }

  sendTrackEvent(e: TrackEvent) {
    validateParams(e);
    normalizeContainers(e);
    this.analytics.sendTrackEvent(e);
  }

  sendOperationalEvent(e: OperationalEvent) {
    validateParams(e);
    normalizeContainers(e);
    this.analytics.sendOperationalEvent(e);
  }

  setTenantInfo(
    tenantType: 'cloudId' | 'orgId' | 'none' | 'opsgenieCustomerId',
    tenantId?: string,
  ) {
    this.analytics.setTenantInfo(tenantType, tenantId);
  }

  setOrgInfo(orgId: string) {
    this.analytics.setOrgInfo(orgId);
  }

  setUserInfo(
    userType: 'atlassianAccount' | 'hashedEmail' | 'trello' | 'opsgenie',
    userId: string,
  ) {
    this.analytics.setUserInfo(userType, userId);
  }

  sendIdentifyEvent(
    userType: 'atlassianAccount' | 'hashedEmail' | 'trello' | 'opsgenie',
    userId: string,
  ) {
    this.analytics.sendIdentifyEvent(userType, userId);
  }

  startUIViewedEvent() {
    this.analytics.startUIViewedEvent();
  }

  stopUIViewedEvent() {
    this.analytics.stopUIViewedEvent();
  }

  // Apdex
  startApdexEvent(e: ApdexStartEvent) {
    this.analytics.startApdexEvent(e);
  }

  stopApdexEvent(e: ApdexStopEvent) {
    this.analytics.stopApdexEvent(e);
  }

  getApdexStart(e: ApdexStartEvent) {
    this.analytics.getApdexStart(e);
  }

  /**
   * Some child components (e.g. @atlaskit/atlassian-switcher) require consumers
   * to fire their events. In these cases, we don't want to do any validation.
   *
   * This function is appropriate inspired by React's dangerouslySetInnerHTML as it
   * bypasses in-built allow-lists. Use with care!
   */
  dangerouslyGetAnalyticsWebClient(): AnalyticsWebClient {
    return this.analytics;
  }

  // Task Sessions
  readonly task: TaskSessionManager;
}

export const Analytics = new AnalyticsWrapper(config, settings);
